import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import Badge from '@mui/material/Badge';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { getShoppingList } from '../utils/shoppingListService';

// --- FIREBASE IMPORTS ---
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase'; 

// Helper function to get initials from a username
const getInitials = (name = '') => {
  if (!name) return '?';
  const nameParts = name.trim().split(/\s+/);
  if (nameParts.length > 1) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase() || '?';
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  // --- AUTH STATES ---
  const [authUser, setAuthUser] = useState(null);
  const [userData, setUserData] = useState(null); // Holds role, username, and photoUrl
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // --- BANNER STATES ---
  const [banner, setBanner] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(true);

  // --- SHOPPING LIST BADGE COUNT ---
  const [shoppingCount, setShoppingCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      try {
        const list = getShoppingList();
        setShoppingCount(list.filter(i => !i.completed).length);
      } catch (e) {}
    };
    updateCount();
    window.addEventListener("shopping_list_updated", updateCount);
    return () => window.removeEventListener("shopping_list_updated", updateCount);
  }, []);

  // --- 1. REAL-TIME AUTH & PROFILE LISTENER ---
  useEffect(() => {
    let unsubscribeSnapshot = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser(user);
        // Listen to Firestore user profile in real-time so photo/username updates reflect immediately
        const userDocRef = doc(db, "users", user.uid);
        unsubscribeSnapshot = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          }
        }, (err) => {
          console.error("Failed to fetch user data for Navbar", err);
        });
      } else {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }
        setAuthUser(null);
        setUserData(null);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }, [auth]);

  // --- 2. FETCH BANNER SETTINGS FROM FIRESTORE ---
  useEffect(() => {
    const fetchBanner = async () => {
        try {
            const bannerRef = doc(db, "settings", "global");
            const bannerSnap = await getDoc(bannerRef);
            
            if (bannerSnap.exists()) {
                const data = bannerSnap.data().announcement;
                // Only set banner if it is marked 'active' and has a message
                if (data && data.active && data.message) {
                    setBanner(data);
                }
            }
        } catch (err) {
            console.log("No banner settings found");
        }
    };
    fetchBanner();
  }, []); 

  // --- 3. HANDLE LOGOUT ---
  const handleLogoutClick = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const isAdmin = userData?.role === 'admin';
  const rawUsername = userData?.username || authUser?.displayName || "";
  const displayUsername = (rawUsername && !rawUsername.includes("@"))
    ? rawUsername
    : (userData?.first_name 
        ? `${userData.first_name} ${userData.last_name || ""}`.trim()
        : (rawUsername ? rawUsername.split("@")[0] : (authUser?.email ? authUser.email.split("@")[0] : "User")));
  const userPhoto = userData?.photoUrl || userData?.photo_url || authUser?.photoURL || "";

  const handleNav = (path) => {
    setMobileDrawerOpen(false);
    navigate(path);
  };

  return (
    <Box sx={{ flexGrow: 1, marginBottom: { xs: 2, sm: 3 } }}>
      
      {/* --- GLOBAL ANNOUNCEMENT BANNER --- */}
      <Collapse in={!!banner && bannerOpen}>
        {banner && (
            <Alert 
                severity={banner.type || "info"} 
                variant="filled"
                action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setBannerOpen(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                }
                sx={{ 
                    borderRadius: 0, 
                    justifyContent: 'center', 
                    '& .MuiAlert-message': { width: '100%', textAlign: 'center' } 
                }}
            >
                {banner.message}
            </Alert>
        )}
      </Collapse>

      <AppBar position="static" sx={{ backgroundColor: '#477491', borderRadius: '0 0 10px 10px' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1.5, sm: 2 } }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontFamily: "'Patrick Hand', cursive",
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              cursor: 'pointer',
              userSelect: 'none'
            }}
            onClick={() => navigate('/recipes')}
          >
            Astrup Family Cookbook
          </Typography>

          {/* DESKTOP BUTTONS (Visible only on md and up) */}
          {authUser ? (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              <Button color="inherit" onClick={() => navigate('/recipes')}>Recipes</Button>
              <Button color="inherit" onClick={() => navigate('/create-recipe')}>Create</Button>
              <Button color="inherit" onClick={() => navigate('/favorites')}>Favorites</Button>
              <Button color="inherit" onClick={() => navigate('/shopping-list')}>
                <Badge badgeContent={shoppingCount} color="warning" sx={{ '& .MuiBadge-badge': { right: -6, top: -2 } }}>
                  Grocery List
                </Badge>
              </Button>
              
              <Button color="inherit" onClick={handleLogoutClick}>Logout</Button>

              <Tooltip title={isAdmin ? `Admin: ${displayUsername}` : `Profile: ${displayUsername}`}>
                <IconButton onClick={() => navigate('/profile')} sx={{ ml: 2, p: 0.5 }}>
                  <Avatar 
                    src={userPhoto} 
                    alt={displayUsername}
                    sx={{ 
                      bgcolor: isAdmin ? '#FF7043' : '#D28415', 
                      width: 38, 
                      height: 38,
                      border: '1.5px solid rgba(255, 255, 255, 0.7)'
                    }}
                  >
                    {getInitials(displayUsername)}
                  </Avatar>
                </IconButton>
              </Tooltip>
              
              {isAdmin && (
                <Typography
                  variant="body2"
                  sx={{ color: '#fff', ml: 1, fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer' }}
                  onClick={() => navigate('/admin')}
                >
                  (Admin)
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button color="inherit" onClick={() => navigate('/login')}>Login</Button>
              <Button color="inherit" onClick={() => navigate('/register')}>Register</Button>
            </Box>
          )}

          {/* MOBILE TOP CONTROLS (Visible only on xs and sm) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
            {authUser && (
              <IconButton onClick={() => navigate('/profile')} sx={{ p: 0.5 }}>
                <Avatar 
                  src={userPhoto} 
                  alt={displayUsername}
                  sx={{ 
                    bgcolor: isAdmin ? '#FF7043' : '#D28415', 
                    width: 32, 
                    height: 32,
                    fontSize: '0.8rem',
                    border: '1.5px solid rgba(255, 255, 255, 0.8)'
                  }}
                >
                  {getInitials(displayUsername)}
                </Avatar>
              </IconButton>
            )}

            <IconButton
              color="inherit"
              edge="end"
              aria-label="menu"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ p: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* MOBILE BOTTOM NAVIGATION BAR (Thumb-friendly navigation on phones) */}
      {authUser && (
        <Paper 
          elevation={10} 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1200, 
            display: { xs: 'block', md: 'none' },
            pb: 'calc(env(safe-area-inset-bottom, 0px))',
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(12px)',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          <BottomNavigation
            showLabels
            value={
              ['/recipes', '/create-recipe', '/favorites', '/shopping-list', '/profile'].includes(location.pathname)
                ? location.pathname 
                : false
            }
            onChange={(event, newValue) => {
              navigate(newValue);
            }}
            sx={{
              bgcolor: 'transparent',
              height: 60,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                py: 0.75,
                color: '#666',
                '&.Mui-selected': {
                  color: '#D28415',
                  fontWeight: 'bold'
                }
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.72rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem'
                }
              }
            }}
          >
            <BottomNavigationAction 
              label="Recipes" 
              value="/recipes" 
              icon={<RestaurantMenuIcon fontSize="small" />} 
            />
            <BottomNavigationAction 
              label="Create" 
              value="/create-recipe" 
              icon={<AddCircleOutlineIcon fontSize="small" />} 
            />
            <BottomNavigationAction 
              label="Favorites" 
              value="/favorites" 
              icon={<FavoriteIcon fontSize="small" />} 
            />
            <BottomNavigationAction 
              label="Grocery" 
              value="/shopping-list" 
              icon={
                <Badge badgeContent={shoppingCount} color="warning" sx={{ '& .MuiBadge-badge': { right: -4, top: -2 } }}>
                  <ShoppingCartIcon fontSize="small" />
                </Badge>
              } 
            />
            <BottomNavigationAction 
              label="Profile" 
              value="/profile" 
              icon={<PersonIcon fontSize="small" />} 
            />
          </BottomNavigation>
        </Paper>
      )}

      {/* MOBILE DRAWER (For extra links, admin panel, feedback, roadmap, and logout) */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: { width: 280, bgcolor: '#faf7f2', p: 2 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
            Menu
          </Typography>
          <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {authUser && (
          <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, mb: 2, border: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              src={userPhoto} 
              sx={{ bgcolor: isAdmin ? '#FF7043' : '#D28415', width: 44, height: 44 }}
            >
              {getInitials(displayUsername)}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {displayUsername}
              </Typography>
              {isAdmin && (
                <Chip label="Admin" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem', mt: 0.25 }} />
              )}
            </Box>
          </Box>
        )}

        <List sx={{ p: 0 }}>
          {authUser ? (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/recipes')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><RestaurantMenuIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Browse Recipes" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/create-recipe')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><AddCircleOutlineIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Create Recipe" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/favorites')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><FavoriteIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Saved Favorites" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/shopping-list')}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Badge badgeContent={shoppingCount} color="warning">
                      <ShoppingCartIcon color="action" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary="Grocery List" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/profile')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><PersonIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Profile & Settings" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/roadmap')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><MapIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Roadmap & Feedback" />
                </ListItemButton>
              </ListItem>

              {isAdmin && (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNav('/admin')}>
                    <ListItemIcon sx={{ minWidth: 40 }}><AdminPanelSettingsIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Admin Dashboard" sx={{ color: '#FF7043', fontWeight: 'bold' }} />
                  </ListItemButton>
                </ListItem>
              )}

              <Divider sx={{ my: 1.5 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => { setMobileDrawerOpen(false); handleLogoutClick(); }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Logout" sx={{ color: 'error.main' }} />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/login')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><LoginIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNav('/register')}>
                  <ListItemIcon sx={{ minWidth: 40 }}><PersonAddIcon color="action" /></ListItemIcon>
                  <ListItemText primary="Register" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default Navbar;